// ABOUTME: Static, non-interactive preview of a dragged builder node (column/row/divided half/section) for the
// ABOUTME: dnd-kit DragOverlay — it mirrors the real layout (grey cards + cells) so you see what you're moving.
import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import { RhDashboardCell } from "./RhDashboardCell";

const cellSx = (theme, colors) => ({
    flex: 1,
    minWidth: 120,
    minHeight: 200,
    py: 2,
    px: 3,
    borderRadius: 4,
    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
    boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
    display: "flex",
});

const cardSx = (theme, colors) => ({
    p: 1.5,
    pb: 3,
    borderRadius: 4,
    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
    display: "flex",
    flexDirection: "column",
    gap: 3,
});

const PreviewCell = ({ column }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <Box sx={cellSx(theme, colors)}>
            <RhDashboardCell chart={column.chart} />
        </Box>
    );
};

const PreviewRow = ({ row }) => (
    <Box sx={{ display: "flex", gap: 3, width: 1 }}>
        {(row.children || []).map((c) => <PreviewCell key={c.id} column={c} />)}
    </Box>
);

const PreviewHalf = ({ half }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <Box sx={{ ...cardSx(theme, colors), flex: half.width ?? 1 }}>
            {(half.children || []).map((r) => <PreviewRow key={r.id} row={r} />)}
        </Box>
    );
};

const PreviewSection = ({ section }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    if (section.divided) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: 1 }}>
                {(section.children || []).map((h) => <PreviewHalf key={h.id} half={h} />)}
            </Box>
        );
    }
    return (
        <Box sx={{ ...cardSx(theme, colors), width: 1 }}>
            {(section.children || []).map((r) => <PreviewRow key={r.id} row={r} />)}
        </Box>
    );
};

export const RhDragPreview = ({ node }) => {
    if (!node) return null;

    // NB: these are the NODE `type` values from baseconfig ("divided_section" has an underscore), not the
    // camelCase dnd-kit data types ("dividedSection").
    let inner = null;
    if (node.type === "column") inner = <Box sx={{ display: "flex", width: 1 }}><PreviewCell column={node} /></Box>;
    else if (node.type === "row") inner = <PreviewRow row={node} />;
    else if (node.type === "divided_section") inner = <Box sx={{ display: "flex", width: 1 }}><PreviewHalf half={node} /></Box>;
    else if (node.type === "section") inner = <PreviewSection section={node} />;
    else return null;

    // dnd-kit sizes the DragOverlay wrapper to the dragged element's measured width/height, so just fill it.
    return (
        <Box sx={{ width: 1, opacity: 0.95, boxShadow: "0 12px 32px rgba(0,0,0,0.28)", borderRadius: 4, cursor: "grabbing" }}>
            {inner}
        </Box>
    );
};
